
import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Search } from 'lucide-react';

interface Announcement {
  id: string;
  title: string;
  description: string | null;
  breeder_id: string;
  breeder_name: string;
}

interface AddTopAuctionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

const fetchAnnouncementsWithProfiles = async (
  queryBuilder: any
): Promise<Announcement[]> => {
  const { data, error } = await queryBuilder;
  if (error) throw error;
  if (!data || data.length === 0) return [];

  const breederIds = [...new Set(data.map((a: any) => a.breeder_id))] as string[];
  const { data: profiles } = await supabase
    .from('profiles')
    .select('id, first_name, last_name, business_name')
    .in('id', breederIds);

  const profileMap = new Map(
    (profiles || []).map((p: any) => [p.id, p])
  );

  return data.map((item: any) => {
    const profile = profileMap.get(item.breeder_id) as any;
    return {
      id: item.id,
      title: item.title,
      description: item.description,
      breeder_id: item.breeder_id,
      breeder_name: profile?.business_name ||
        `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() ||
        'Unknown',
    };
  });
};

export const AddTopAuctionDialog = ({ open, onOpenChange, onSuccess }: AddTopAuctionDialogProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAuction, setSelectedAuction] = useState<Announcement | null>(null);
  const { toast } = useToast();

  const { data: recentAuctions } = useQuery({
    queryKey: ['recent-auctions'],
    queryFn: () =>
      fetchAnnouncementsWithProfiles(
        supabase
          .from('announcements')
          .select('id, title, description, breeder_id')
          .order('created_at', { ascending: false })
          .limit(3)
      ),
    enabled: open,
  });

  const { data: searchResults, isLoading } = useQuery({
    queryKey: ['search-auctions', searchTerm],
    queryFn: () =>
      fetchAnnouncementsWithProfiles(
        supabase
          .from('announcements')
          .select('id, title, description, breeder_id')
          .ilike('title', `%${searchTerm}%`)
          .limit(10)
      ),
    enabled: open && searchTerm.length > 0,
  });

  const addTopAuction = useMutation({
    mutationFn: async () => {
      if (!selectedAuction) throw new Error('No auction selected');

      const { data: user, error: userError } = await supabase.auth.getUser();
      if (userError || !user.user) throw new Error('User not authenticated');

      const { data: maxOrderData } = await supabase
        .from('top_auctions')
        .select('display_order')
        .order('display_order', { ascending: false })
        .limit(1);

      const nextOrder = maxOrderData && maxOrderData.length > 0
        ? maxOrderData[0].display_order + 1
        : 1;

      const { error } = await supabase
        .from('top_auctions')
        .insert({
          announcement_id: selectedAuction.id,
          selected_by: user.user.id,
          display_order: nextOrder,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Success", description: "Top auction added successfully." });
      onSuccess();
      onOpenChange(false);
      setSelectedAuction(null);
      setSearchTerm('');
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to add top auction. It may already be selected.", variant: "destructive" });
    },
  });

  const renderAuctionItem = (auction: Announcement) => (
    <div
      key={auction.id}
      className={`p-3 border-b cursor-pointer hover:bg-muted/50 ${
        selectedAuction?.id === auction.id ? 'bg-primary/10' : ''
      }`}
      onClick={() => setSelectedAuction(auction)}
    >
      <p className="font-medium text-sm">{auction.title}</p>
      <p className="text-xs text-muted-foreground">{auction.breeder_name}</p>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Top Auction</DialogTitle>
          <DialogDescription>
            Search and select an auction to feature on the auctions page.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="search-auction">Search Auctions</Label>
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="search-auction"
                placeholder="Search by auction title..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {searchTerm ? (
            <div className="max-h-48 overflow-y-auto border rounded-md">
              {isLoading ? (
                <p className="p-4 text-center text-sm text-muted-foreground">Searching...</p>
              ) : searchResults?.length === 0 ? (
                <p className="p-4 text-center text-sm text-muted-foreground">No auctions found.</p>
              ) : (
                searchResults?.map(renderAuctionItem)
              )}
            </div>
          ) : recentAuctions && recentAuctions.length > 0 ? (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Recent Auctions</p>
              <div className="max-h-48 overflow-y-auto border rounded-md">
                {recentAuctions.map(renderAuctionItem)}
              </div>
            </div>
          ) : null}

          {selectedAuction && (
            <div className="p-3 border rounded-md bg-primary/10">
              <p className="font-medium text-sm">Selected:</p>
              <p className="text-sm mt-1">{selectedAuction.title}</p>
              <p className="text-xs text-muted-foreground">{selectedAuction.breeder_name}</p>
            </div>
          )}

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => addTopAuction.mutate()}
              disabled={!selectedAuction || addTopAuction.isPending}
            >
              Add Top Auction
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
